import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import PaletteIcon from '@mui/icons-material/PaletteOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import Button from '../Button';
import getRedirectUrlAfterLogin from 'utils/getRedirectUrlAfterLogin';

const useStyles = makeStyles((theme) => ({
  section: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
    backgroundColor: 'rgba(245, 245, 245, 0.3)',
  },
  container: {
    maxWidth: '1280px',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(8),
    animation: '$fadeIn 0.6s ease-out',
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: 700,
    color: '#121212',
    [theme.breakpoints.up('md')]: {
      fontSize: '2.25rem',
    },
  },
  timelineDesktop: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
      marginBottom: theme.spacing(8),
    },
  },
  timelineLine: {
    position: 'absolute',
    top: '55px',
    left: '12.5%',
    right: '12.5%',
    height: '2px',
    backgroundColor: '#F5F5F5',
  },
  timelineNumber: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#D91A5B',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '1.125rem',
    position: 'relative',
    zIndex: 10,
    margin: '0 auto 24px',
  },
  stepCard: {
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #F5F5F5',
    transition: 'all 0.3s ease',
    height: '100%',
    textAlign: 'center',
    '&:hover': {
      borderColor: '#D91A5B',
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    },
  },
  iconCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#F5F5F5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  icon: {
    fontSize: '32px',
    color: '#D91A5B',
  },
  stepLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#616161',
    marginBottom: theme.spacing(1),
  },
  stepTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#121212',
    marginBottom: theme.spacing(1.5),
  },
  stepDescription: {
    fontSize: '0.875rem',
    color: '#616161',
    lineHeight: 1.7,
  },
  timelineMobile: {
    display: 'block',
    marginBottom: theme.spacing(6),
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  mobileStep: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  mobileTimeline: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  mobileNumber: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#D91A5B',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '0.875rem',
    flexShrink: 0,
  },
  mobileLine: {
    width: '2px',
    flex: 1,
    backgroundColor: '#F5F5F5',
    marginTop: theme.spacing(1),
  },
  mobileContent: {
    flex: 1,
    paddingBottom: theme.spacing(3),
  },
  mobileCard: {
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #F5F5F5',
  },
  ctaContainer: {
    textAlign: 'center',
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const steps = [
  {
    icon: PersonAddIcon,
    number: '01',
    title: 'Create Your Account',
    description: 'Sign up for free in seconds – no credit card needed',
  },
  {
    icon: DescriptionIcon,
    number: '02',
    title: 'Choose a Template',
    description: 'Select from 300+ expert-validated templates',
  },
  {
    icon: PaletteIcon,
    number: '03',
    title: 'Customize Your Diagram',
    description: 'Use drag-and-drop PowerEdit tools to create',
  },
  {
    icon: DownloadIcon,
    number: '04',
    title: 'Export and Share',
    description: 'Download in high-resolution formats (PNG, PDF, SVG, TIFF)',
  },
];

export default function HowItWorks() {
  const classes = useStyles();

  const handleCreateNowClick = () => {
    window.location.href = getRedirectUrlAfterLogin();
  };

  return (
    <section className={classes.section}>
      <Container className={classes.container}>
        <Box className={classes.header}>
          <Typography className={classes.title} component="h3">
            How to Use Mind The Graph's Science Diagram Maker
          </Typography>
        </Box>

        {/* Desktop Timeline */}
        <Box className={classes.timelineDesktop}>
          <Box style={{ position: 'relative', marginBottom: '48px' }}>
            <Box className={classes.timelineLine} />
            <Grid container spacing={4}>
              {steps.map((step, index) => (
                <Grid item xs={3} key={index}>
                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box className={classes.timelineNumber}>{step.number}</Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={3} key={index}>
                <Box className={classes.stepCard}>
                  <Box className={classes.iconCircle}>
                    <step.icon className={classes.icon} />
                  </Box>
                  <Typography className={classes.stepLabel}>Step {index + 1}</Typography>
                  <Typography className={classes.stepTitle}>{step.title}</Typography>
                  <Typography className={classes.stepDescription}>{step.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Mobile Timeline */}
        <Box className={classes.timelineMobile}>
          {steps.map((step, index) => (
            <Box className={classes.mobileStep} key={index}>
              <Box className={classes.mobileTimeline}>
                <Box className={classes.mobileNumber}>{step.number}</Box>
                {index < steps.length - 1 && <Box className={classes.mobileLine} />}
              </Box>
              <Box className={classes.mobileContent}>
                <Box className={classes.mobileCard}>
                  <Box className={classes.iconCircle}>
                    <step.icon className={classes.icon} />
                  </Box>
                  <Typography className={classes.stepLabel}>Step {index + 1}</Typography>
                  <Typography className={classes.stepTitle}>{step.title}</Typography>
                  <Typography className={classes.stepDescription}>{step.description}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Box className={classes.ctaContainer}>
          <Button size="large" variant="contained" onClick={handleCreateNowClick} component="a">
            Create Now – It's Free
          </Button>
        </Box>
      </Container>
    </section>
  );
}
